import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { ChevronLeft, Building, Loader2, ArrowRight, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../../lib/apiError';
import { READY_PENDING_KEY } from './AccountReadyStep';
import OnboardingStepIndicator from './OnboardingStepIndicator';

// Phase 3 UX (Feb 2026): Manager first-time flow was trimmed to the bare
// minimum required to reach "Find Cover" — just the branch name and
// postcode, followed by a plain-language confirmation.  Operating area,
// role and payment steps were removed from the blocking gate:
//   • operating area → derivable / can be edited later from Profile
//   • manager role   → optional / defaults sensibly for the pilot
//   • payment method → collected at post-shift time (see PostShift.jsx)
// The old 5-step wizard used to also ask for city/area, business role and
// card details up-front, which the Phase 3 audit flagged as friction.
const UK_POSTCODE_RE = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;

export default function ManagerOnboarding() {
  const { api, user, refreshUser } = useAuth();
  const [step, setStep] = useState(1); // 1: enter, 2: confirm
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    defaultBranchName: user?.defaultBranchName || user?.businessName || '',
    postcode: (user?.postcode || '').toUpperCase(),
    businessType: user?.businessType || 'post_office',
    multiBranch: false,
    additionalBranches: [], // [{name, postcode}]
  });

  const validate = () => {
    const next = {};
    if (!form.defaultBranchName.trim()) next.defaultBranchName = 'Enter your branch name.';
    if (!form.postcode.trim()) next.postcode = 'Enter a valid UK postcode.';
    else if (!UK_POSTCODE_RE.test(form.postcode.trim())) next.postcode = 'Enter a valid UK postcode.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleContinueToConfirm = () => {
    if (!validate()) return;
    setStep(2);
  };

  const handleConfirmAndSave = async () => {
    setSaving(true);
    try {
      await api.patch('/users/me', {
        defaultBranchName: form.defaultBranchName.trim(),
        postcode: form.postcode.trim().toUpperCase(),
        businessType: form.businessType,
      });
      // iter95 — persist any additional branches captured during
      // multi-branch onboarding. Fire-and-forget so a per-branch API
      // failure doesn't block onboarding completion; the manager can
      // always add branches later from Settings.
      const extras = (form.additionalBranches || []).filter(
        b => b.name?.trim() && b.postcode?.trim() && UK_POSTCODE_RE.test(b.postcode.trim())
      );
      for (const b of extras) {
        try {
          await api.post('/branches', {
            name: b.name.trim(),
            postcode: b.postcode.trim().toUpperCase(),
          });
        } catch { /* non-blocking */ }
      }
      await api.patch('/users/me/onboarding-complete');
      // Mark that this user has just completed setup so the gate shows the
      // one-time "Your branch is ready" hand-off. Returning users who
      // already onboarded on a previous session won't have this flag.
      if (user?.id) {
        try { localStorage.setItem(READY_PENDING_KEY(user.id), '1'); } catch { /* storage may be blocked */ }
      }
      await refreshUser();
    } catch (err) {
      toast.error(formatApiError(err, 'We could not save your details. Please try again.'));
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F1F5F9] flex items-center justify-center p-4" data-testid="manager-onboarding">
      <Card className="w-full max-w-md border-0 shadow-lg">
        <CardContent className="p-8">
          {/* Manager 5-step map: 1) Branch setup, 2) Legal, 3) Trial ready,
              4) Install, 5) Notifications. AccountReady is a hand-off. */}
          <OnboardingStepIndicator current={1} role="manager" />

          {step === 1 && (
            <>
              <div className="flex items-center gap-3 mb-2">
                <div className="h-11 w-11 bg-[#0f1b3d] rounded-xl flex items-center justify-center shrink-0">
                  <Building className="h-6 w-6 text-white" />
                </div>
                <h2 className="text-xl font-bold text-slate-900">Set up your branch</h2>
              </div>
              <p className="text-base text-slate-600 mb-6">
                Just the essentials — you can add more branch details later from your profile. Only workers you invite to your shifts see this address.
              </p>

              <div className="space-y-5" data-testid="onboard-step-branch">
                <div className="space-y-1.5">
                  <Label htmlFor="branchName" className="text-base font-medium text-slate-800">Branch name</Label>
                  <Input
                    id="branchName"
                    value={form.defaultBranchName}
                    onChange={e => { setForm(f => ({ ...f, defaultBranchName: e.target.value })); if (errors.defaultBranchName) setErrors(er => ({ ...er, defaultBranchName: undefined })); }}
                    placeholder="e.g., Camden High Street"
                    className="h-12 rounded-xl text-base"
                    data-testid="onboard-branch-name"
                    aria-invalid={!!errors.defaultBranchName}
                    aria-describedby={errors.defaultBranchName ? 'branch-error' : undefined}
                  />
                  {errors.defaultBranchName && (
                    <p id="branch-error" className="text-sm text-red-600" data-testid="branch-name-error">{errors.defaultBranchName}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="postcode" className="text-base font-medium text-slate-800">Branch postcode</Label>
                  <Input
                    id="postcode"
                    value={form.postcode}
                    onChange={e => { setForm(f => ({ ...f, postcode: e.target.value.toUpperCase() })); if (errors.postcode) setErrors(er => ({ ...er, postcode: undefined })); }}
                    placeholder="e.g., NW1 8QS"
                    className="h-12 rounded-xl text-base uppercase"
                    data-testid="onboard-postcode"
                    aria-invalid={!!errors.postcode}
                    aria-describedby={errors.postcode ? 'postcode-error' : undefined}
                    autoComplete="postal-code"
                  />
                  {errors.postcode && (
                    <p id="postcode-error" className="text-sm text-red-600" data-testid="postcode-error">{errors.postcode}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="businessType" className="text-base font-medium text-slate-800">Business type</Label>
                  <select
                    id="businessType"
                    value={form.businessType}
                    onChange={e => setForm(f => ({ ...f, businessType: e.target.value }))}
                    className="h-12 w-full rounded-xl border border-slate-200 bg-white px-3 text-base"
                    data-testid="onboard-business-type"
                  >
                    <option value="post_office">Post Office</option>
                    <option value="convenience_store">Convenience store</option>
                    <option value="pharmacy">Pharmacy</option>
                    <option value="cafe">Café / restaurant</option>
                    <option value="retail_other">Other retail</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="onboard-multi-branch-section">
                  <p className="text-sm font-medium text-slate-800">Do you manage more than one branch?</p>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={form.multiBranch ? 'outline' : 'default'}
                      size="sm"
                      className={form.multiBranch ? '' : 'bg-[#0f1b3d] hover:bg-[#1a2d5a] text-white'}
                      onClick={() => setForm(f => ({ ...f, multiBranch: false, additionalBranches: [] }))}
                      data-testid="onboard-multi-branch-no"
                    >No</Button>
                    <Button
                      type="button"
                      variant={form.multiBranch ? 'default' : 'outline'}
                      size="sm"
                      className={form.multiBranch ? 'bg-[#0f1b3d] hover:bg-[#1a2d5a] text-white' : ''}
                      onClick={() => setForm(f => ({ ...f, multiBranch: true }))}
                      data-testid="onboard-multi-branch-yes"
                    >Yes</Button>
                  </div>
                  {form.multiBranch && (
                    <div className="space-y-2 pt-2">
                      {form.additionalBranches.map((b, i) => (
                        <div key={i} className="grid grid-cols-2 gap-2" data-testid={`additional-branch-${i}`}>
                          <Input
                            value={b.name}
                            onChange={e => setForm(f => {
                              const list = [...f.additionalBranches];
                              list[i] = { ...list[i], name: e.target.value };
                              return { ...f, additionalBranches: list };
                            })}
                            placeholder="Branch name"
                            className="h-10 rounded-lg text-sm"
                          />
                          <Input
                            value={b.postcode}
                            onChange={e => setForm(f => {
                              const list = [...f.additionalBranches];
                              list[i] = { ...list[i], postcode: e.target.value.toUpperCase() };
                              return { ...f, additionalBranches: list };
                            })}
                            placeholder="Postcode"
                            className="h-10 rounded-lg text-sm uppercase"
                          />
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setForm(f => ({ ...f, additionalBranches: [...f.additionalBranches, { name: '', postcode: '' }] }))}
                        className="w-full h-10 rounded-lg border-dashed"
                        data-testid="add-another-branch-btn"
                      >+ Add another branch</Button>
                      <p className="text-xs text-slate-500">You can always add more branches later from Settings.</p>
                    </div>
                  )}
                </div>
              </div>

              <Button
                onClick={handleContinueToConfirm}
                className="w-full h-12 rounded-full mt-7 bg-[#0f1b3d] hover:bg-[#1a2d5a] text-white text-base font-semibold"
                data-testid="onboard-continue-btn"
              >
                Continue <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            </>
          )}

          {step === 2 && (
            <>
              <div className="flex items-center gap-3 mb-2">
                <div className="h-11 w-11 bg-[#0f1b3d] rounded-xl flex items-center justify-center shrink-0">
                  <MapPin className="h-6 w-6 text-white" />
                </div>
                <h2 className="text-xl font-bold text-slate-900">Is this your branch?</h2>
              </div>
              <p className="text-base text-slate-600 mb-6">
                Please confirm your branch details before continuing.
              </p>

              <div className="rounded-xl border-2 border-slate-200 bg-slate-50 p-5 mb-6" data-testid="onboard-confirm-summary">
                <p className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-1">Branch</p>
                <p className="text-lg font-semibold text-slate-900 mb-3" data-testid="confirm-branch-name">{form.defaultBranchName.trim()}</p>
                <p className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-1">Postcode</p>
                <p className="text-lg font-semibold text-slate-900" data-testid="confirm-postcode">{form.postcode.trim().toUpperCase()}</p>
              </div>

              <div className="space-y-3">
                <Button
                  onClick={handleConfirmAndSave}
                  disabled={saving}
                  className="w-full h-12 rounded-full bg-[#DA291C] hover:bg-[#b91f13] text-white text-base font-semibold disabled:opacity-60"
                  data-testid="onboard-confirm-btn"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (<>Yes, continue <ArrowRight className="h-4 w-4 ml-1.5" /></>)}
                </Button>
                <Button
                  onClick={() => setStep(1)}
                  variant="outline"
                  disabled={saving}
                  className="w-full h-12 rounded-full text-base font-semibold"
                  data-testid="onboard-edit-btn"
                >
                  <ChevronLeft className="h-4 w-4 mr-1.5" /> Edit details
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
